<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useQuasar } from 'quasar';
import { useSchoolStore } from '@/stores/school';
import type StudentSearchCriteria from '@/types/StudentSearchCriteria';

defineProps<{
    loading: boolean;
}>();

onMounted(() => {
    window.scrollTo(0, 0);
});

const email = ref('');
const cusId = ref<string>('');
const firstName = ref('');
const lastName = ref('');
const schoolSelections = ref<number[]>([]);
const emit = defineEmits<{(event:'send:request', value: StudentSearchCriteria): void}>();

const $q = useQuasar();
const schoolStore = useSchoolStore();
const schools = schoolStore.schools.map((item) => ({ label: item.name, value: item.id }));
const ruleEmailAddress = (val:string) => ((!val || val.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/) !== null) || 'Invalid email address');

const validateEmail = (): object | null => (email.value ? { email: email.value } : null);

const validateCusId = (): object | null => (cusId.value ? { studentId: parseInt(cusId.value, 10) } : null);

const validateName = (): object | null => (
    firstName.value?.trim() && lastName.value?.trim() ? { firstName: firstName.value, lastName: lastName.value } : null
);

const validateInputs = (): object | null => {
    const validatedEmail = validateEmail();
    const validatedCusId = validateCusId();
    const validatedName = validateName();

    if (!validatedEmail && !validatedCusId && !validatedName) {
        $q.notify({
            color: 'negative',
            icon: 'warning',
            message: 'Please enter an email, student ID, or a first and last name',
        });
        return null;
    }

    if (
        (validatedEmail && validatedCusId)
        || (validatedEmail && (firstName.value || lastName.value))
        || (validatedCusId && (firstName.value || lastName.value))
    ) {
        $q.notify({
            color: 'negative',
            icon: 'warning',
            message: 'Please enter either an email, a student ID, or a first and last name, not a combination',
        });
        return null;
    }

    return { ...validatedEmail, ...validatedCusId, ...validatedName };
};

const submit = async () => {
    const validatedInputs = validateInputs();

    if (validatedInputs != null) {
        const criteria: StudentSearchCriteria = {
            ...validatedInputs,
            flightSchoolIds: schoolSelections.value,
            courseAcronyms: [],
        };

        emit('send:request', criteria);
    }
};
</script>
<template>
    <q-form
        class="student-search-form container q-gutter-y-lg q-py-md bg-white"
        :class="$q.screen.gt.xs ? 'q-px-xl' : 'q-px-lg'"
        @submit="submit"
    >
        <div class="input-wrapper">
            <div class="input-label text-weight-bold">
                Email
            </div>
            <q-input
                v-model="email"
                class="email-input"
                type="email"
                square
                outlined
                dense
                :rules="[ruleEmailAddress]"
                lazy-rules
            />
        </div>
        <div class="divider row q-pt-sm">
            <div class="col">
                <q-separator />
            </div>
            <div class="col-2 text-center">
                or
            </div>
            <div class="col">
                <q-separator />
            </div>
        </div>
        <div class="input-wrapper">
            <div class="input-label text-weight-bold">
                Student ID
            </div>
            <q-input
                v-model="cusId"
                class="cus-id-input"
                type="number"
                inputmode="numeric"
                square
                outlined
                dense
            />
        </div>
        <div class="divider row q-pt-md">
            <div class="col">
                <q-separator />
            </div>
            <div class="col-2 text-center">
                or
            </div>
            <div class="col">
                <q-separator />
            </div>
        </div>
        <div class="input-wrapper">
            <div class="input-label text-weight-bold">
                First Name
            </div>
            <q-input
                v-model="firstName"
                class="first-name-input"
                square
                outlined
                dense
            />
        </div>
        <div class="input-wrapper">
            <div class="input-label text-weight-bold">
                Last Name
            </div>
            <q-input
                v-model="lastName"
                class="last-name-input"
                square
                outlined
                dense
            />
        </div>
        <div class="input-wrapper">
            <div class="input-label less-padding text-weight-bold">
                School
            </div>
            <div class="subtext q-pb-md">
                All available schools will be searched if none is selected.
            </div>
            <q-select
                v-model="schoolSelections"
                class="school-selector"
                :options="schools"
                clearable
                outlined
                square
                dense
                multiple
                options-dense
                emit-value
                map-options
                label="Select"
            />
        </div>
        <div class="buttons row q-pb-lg">
            <div class="col-grow q-pt-xs">
                <q-btn
                    id="back-btn"
                    label="Go Back"
                    type="button"
                    class="back-btn fsum-button-outlined float-left"
                    icon="chevron_left"
                    no-caps
                    flat
                    align="left"
                    @click="$router.back()"
                />
            </div>
            <div class="col-shrink q-pt-xs">
                <q-btn
                    id="submit-btn"
                    label="Search"
                    type="submit"
                    class="submit-btn fsum-button-dark-blue float-right"
                    no-caps
                    flat
                    :loading="loading"
                />
            </div>
        </div>
    </q-form>
</template>
<style scoped lang="scss">
.student-search-form {
    max-width: 750px;
    border-radius: 4px;
    font-size: 16px;
    margin: auto;

    .divider .col {
        margin: auto;

        :deep(hr) {
            color: var(--fsum-badge-gray);
        }
    }
    .input-wrapper .cus-id-input {
        :deep(input::-webkit-outer-spin-button),
        :deep(input::-webkit-inner-spin-button) {
            -webkit-appearance: none;
            margin: 0;
        }
    }
    .input-label {
        padding-bottom: 12px;

        &.less-padding {
            padding-bottom: 0;
        }
    }
    .subtext {
        font-size: 13px;
        color: var(--fsum-med-light-gray);
    }
    :deep(.school-selector.q-field__label) {
        color: var(--fsum-black)
    }
    button.submit-btn {
        font-size: 16px;
    }
    .back-btn {
        padding-left: 5px;
        font-size: 16px;
    }
}
</style>
